// Grid A* pathfinding (MOB-3, D11). PURE (ENG-1).
//
// The deliberate, node-budgeted planner used ONLY by villagers/golems/warden/raid
// mobs — everything else uses the greedy steer (steer.ts). It searches over
// *standable* cells (2-high air gap on a solid floor), following terrain by
// stepping up 1 block or dropping up to `maxDrop`, and treats doors as passable
// because a door block is non-solid (BLK shape 'door'). Capped at `budget`
// expanded nodes so a hopeless search can never stall a tick.
export interface PathWorld {
  isSolid(x: number, y: number, z: number): boolean;
  /** Optional hazard probe (lava/fire) — a path never lands on one. */
  isHazard?(x: number, y: number, z: number): boolean;
}

export interface Vec3 { x: number; y: number; z: number; }

export interface PathOpts {
  /** Max expanded nodes before giving up (ARCH: 200/call). */
  budget?: number;
  /** Deepest drop (blocks) a step may descend. */
  maxDrop?: number;
  /** Stop once within this many blocks of the goal (goal cell may be unstandable). */
  reach?: number;
}

const DIRS: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** A mob can stand with its feet at (x,y,z): body clear (2 high), solid floor,
 *  no hazard. This is the node predicate the whole search runs over. */
export function standable(w: PathWorld, x: number, y: number, z: number): boolean {
  if (w.isSolid(x, y, z) || w.isSolid(x, y + 1, z)) return false;
  if (!w.isSolid(x, y - 1, z)) return false;
  return !(w.isHazard?.(x, y, z) ?? false);
}

/** Resolve the standable feet-Y when moving into column (nx,nz) from height `y`:
 *  flat, one step up (with headroom), or a drop up to `maxDrop`; null if none. */
function stepY(w: PathWorld, nx: number, y: number, nz: number, maxDrop: number): number | null {
  // Step up one (needs headroom above the current cell to duck under).
  if (standable(w, nx, y + 1, nz) && !w.isSolid(nx, y + 2, nz)) return y + 1;
  if (standable(w, nx, y, nz)) return y;
  for (let d = 1; d <= maxDrop; d++) if (standable(w, nx, y - d, nz)) return y - d;
  return null;
}

const key = (x: number, y: number, z: number): number =>
  ((x & 0x3ff) << 20) | ((y & 0x3ff) << 10) | (z & 0x3ff);

/** A* from `start` feet-cell to (near) `goal`. Returns feet-cells start→goal, or
 *  null if unreachable within the budget. */
export function findPath(w: PathWorld, start: Vec3, goal: Vec3, opts: PathOpts = {}): Vec3[] | null {
  const budget = opts.budget ?? 200;
  const maxDrop = opts.maxDrop ?? 3;
  const reach = opts.reach ?? 0;
  const sx = Math.floor(start.x), sy = Math.floor(start.y), sz = Math.floor(start.z);
  const gx = Math.floor(goal.x), gy = Math.floor(goal.y), gz = Math.floor(goal.z);
  const h = (x: number, y: number, z: number): number => Math.abs(x - gx) + Math.abs(y - gy) + Math.abs(z - gz);

  interface Rec { x: number; y: number; z: number; g: number; f: number; parent: number; }
  const nodes: Rec[] = [{ x: sx, y: sy, z: sz, g: 0, f: h(sx, sy, sz), parent: -1 }];
  const open: number[] = [0]; // indices into `nodes`, kept as a simple min-scan queue
  const best = new Map<number, number>([[key(sx, sy, sz), 0]]);
  let expanded = 0;
  let closestIdx = 0; // fallback: nearest node to the goal if it's unreachable

  while (open.length > 0 && expanded < budget) {
    // Pop the lowest-f open node (linear scan — budget is small).
    let pick = 0;
    for (let i = 1; i < open.length; i++) if (nodes[open[i]!]!.f < nodes[open[pick]!]!.f) pick = i;
    const idx = open.splice(pick, 1)[0]!;
    const cur = nodes[idx]!;
    expanded++;
    if (h(cur.x, cur.y, cur.z) < h(nodes[closestIdx]!.x, nodes[closestIdx]!.y, nodes[closestIdx]!.z)) closestIdx = idx;

    // Reached the goal (exact, or within the reach slack).
    if (Math.abs(cur.x - gx) + Math.abs(cur.z - gz) <= reach && Math.abs(cur.y - gy) <= 1) {
      return reconstruct(nodes, idx);
    }

    for (const [dx, dz] of DIRS) {
      const nx = cur.x + dx, nz = cur.z + dz;
      const ny = stepY(w, nx, cur.y, nz, maxDrop);
      if (ny === null) continue;
      const k = key(nx, ny, nz);
      const ng = cur.g + 1;
      const prev = best.get(k);
      if (prev !== undefined && nodes[prev]!.g <= ng) continue;
      const rec: Rec = { x: nx, y: ny, z: nz, g: ng, f: ng + h(nx, ny, nz), parent: idx };
      const ni = nodes.push(rec) - 1;
      best.set(k, ni);
      open.push(ni);
    }
  }
  // Goal unreachable within budget: hand back the best partial path if we made
  // real progress toward it (lets a villager still shuffle the right way).
  if (closestIdx !== 0 && h(nodes[closestIdx]!.x, nodes[closestIdx]!.y, nodes[closestIdx]!.z) < h(sx, sy, sz)) {
    return reconstruct(nodes, closestIdx);
  }
  return null;
}

function reconstruct(nodes: Array<{ x: number; y: number; z: number; parent: number }>, idx: number): Vec3[] {
  const path: Vec3[] = [];
  for (let i = idx; i !== -1; i = nodes[i]!.parent) path.push({ x: nodes[i]!.x, y: nodes[i]!.y, z: nodes[i]!.z });
  path.reverse();
  return path;
}
